/**
 * Mobile Haptic Feedback Utility
 * 
 * Provides centralized haptic feedback for mobile devices using the Web Vibration API.
 * Only triggers on mobile devices (coarse pointer) and fails silently if unsupported.
 */

export type HapticType =
  | "light"
  | "medium"
  | "heavy"
  | "success"
  | "error";

/**
 * Device detection: checks if device has coarse pointer (mobile/touch device)
 */
const isMobile = (): boolean => {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(pointer: coarse)").matches;
};

/**
 * Checks if vibration API is supported
 */
const isVibrationSupported = (): boolean => {
  return typeof navigator !== "undefined" && "vibrate" in navigator;
};

/**
 * Vibration patterns for different haptic types
 */
const HAPTIC_PATTERNS: Record<HapticType, number | number[]> = {
  light: [10],
  medium: [20],
  heavy: [40],
  success: [10, 30, 10],
  error: [30, 20, 30],
};

/**
 * Triggers haptic feedback on mobile devices
 * 
 * @param type - The type of haptic feedback to trigger
 * 
 * Behavior:
 * - Only triggers on mobile devices (coarse pointer)
 * - Fails silently if vibration API is not supported
 * - Non-blocking and lightweight
 */
export function triggerHaptic(type: HapticType): void {
  // Only trigger on mobile devices
  if (!isMobile()) {
    return;
  }

  // Check if vibration API is supported
  if (!isVibrationSupported()) {
    return;
  }

  // Get vibration pattern for the haptic type
  const pattern = HAPTIC_PATTERNS[type];

  // Trigger vibration (non-blocking)
  try {
    navigator.vibrate(pattern);
  } catch (error) {
    // Fail silently - haptics are optional enhancement
    // Don't log errors to avoid console spam
  }
}
